/**
 * Pure, framework-agnostic helpers for converting between cell values
 * and `<input type="number">` draft strings.
 *
 * Used by inline-edit table cells to normalize raw user input into the
 * `null | number` shape the backend expects, and to compare drafts
 * against the original row values without false-positive diffs caused
 * by `"5" !== 5`.
 */

export const toDraftValue = (value) => (value === null || value === undefined ? "" : String(value));

export const toNullableNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
};

export const areNullableNumbersEqual = (left, right) => {
  const a = toNullableNumber(left);
  const b = toNullableNumber(right);
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  if (a === null || b === null) return a === b;
  return a === b;
};

/**
 * Parse a draft string for an inline-editable numeric field.
 *
 * @returns {{ value: number | null } | { error: string }}
 */
export const parseDraftNumber = (
  rawValue,
  { label, min = 0, max = null, allowBlank = true } = {},
) => {
  const value = typeof rawValue === "string" ? rawValue.trim() : rawValue;

  if (value === "" || value === null || value === undefined) {
    if (allowBlank) return { value: null };
    return { error: `${label} is required.` };
  }

  const n = Number(value);
  if (!Number.isFinite(n)) return { error: `${label} must be a valid number.` };
  if (min !== null && n < min) return { error: `${label} must be ${min} or greater.` };
  if (max !== null && n > max) return { error: `${label} must be ${max} or less.` };
  return { value: n };
};
